import './OpenLock.css'

import { IonCol, IonContent, IonGrid, IonImg, IonPage, IonRefresher, IonRefresherContent, IonRow, IonSelect, IonSelectOption, IonSpinner, IonText, useIonAlert } from '@ionic/react'
import { useT } from '@transifex/react'
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings'
import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import DummyHeader from '../components/DummyHeader'
import useQlocxDevices from '../hooks/useQlocxDevices'
import { resetCustomer, setCustomer } from '../redux/customerSlice'
import request from '../request'
import { selectCustomer, selectSession } from '../store'
import translationMappings from '../transifex/translationMappings'

const qlocxw = 'https://iframe-assets.s3-eu-west-1.amazonaws.com/images/qlocxw.webp'
const qlocx = 'https://iframe-assets.s3-eu-west-1.amazonaws.com/images/qlocx_logo_full.webp'
const im = 'https://iframe-assets.s3-eu-west-1.amazonaws.com/images/dash-button.webp'
const imDisabled = 'https://iframe-assets.s3-eu-west-1.amazonaws.com/images/dash-button-disabled.webp'
const imLoading = 'https://iframe-assets.s3-eu-west-1.amazonaws.com/images/dash-button-loading.gif'
const imFail = 'https://iframe-assets.s3-eu-west-1.amazonaws.com/images/dash-button-fail.webp'
const imOpen = 'https://iframe-assets.s3-eu-west-1.amazonaws.com/images/dash-button-open.webp'

const preloadedImgs = [qlocxw, qlocx, im, imDisabled, imLoading, imFail, imOpen]

preloadedImgs.forEach((img) => {
  const preloadedImg = new Image()
  preloadedImg.src = img
})

const errorsToTriggerOpenSettings = ['missing_permission:ACCESS_FINE_LOCATION', 'missing_permission:ACCESS_BLUETOOTH', 'missing_permission:BLUETOOTH_SCAN']

export default function OpenLock() {
  const t = useT()
  const customer = useSelector(selectCustomer)
  const { theme, phoneCharacteristics } = useSelector(selectSession)
  const dispatch = useDispatch()
  const [scan, open, testOpen, eaOpen, remoteOpen, clusterOpen, resetDevices, { loading, error, data }] = useQlocxDevices(customer?.customer._id, !!customer?.customer?.roles?.tester, customer?.allAccesses)
  const [presentAlert] = useIonAlert()
  const lockSelectionRef = useRef<HTMLIonSelectElement>()

  const getCustomer = () => request(`/customer?phone_model=${phoneCharacteristics.phoneModel}&app_version=${phoneCharacteristics.appVersion}`).then((r) => dispatch(setCustomer(r)))

  const onIonRefresh = (e: any) => {
    getCustomer().finally(e.detail.complete)
  }

  const isOpeningOrOpened = useMemo(() => data?.action === 'open' || data?.action === 'eaOpen' || data?.action === 'remoteOpen' || data?.action === 'testOpen', [data])
  const isFetchingCustomer = useMemo(() => !customer || !customer.allAccesses, [customer])
  const isDisabled = useMemo(() => !customer?.allAccesses?.length && !customer?.customer?.roles?.tester?.active, [customer])

  useEffect(() => {
    if (error) {
      const openSettingsButtons = [
        {
          text: t('Settings'),
          handler: () => {
            NativeSettings.open({
              optionAndroid: AndroidSettings.ApplicationDetails,
              optionIOS: IOSSettings.App,
            })
          },
        },
        t('Cancel'),
      ]
      const buttons = errorsToTriggerOpenSettings.includes(error.message) ? openSettingsButtons : [t('OK')]

      presentAlert({
        header: t('Error'),
        message: t(translationMappings[error.message] || error.message),
        buttons,
        onWillDismiss: () => resetDevices(false),
      })
      if (error.message === 'generic' || error.message === 'timeout' || error.message === 'No locks found') {
        dispatch(resetCustomer())
        getCustomer()
      }
    }
  }, [error])

  useEffect(() => {
    if (loading || !customer) return

    if (data?.action === 'scan' && data?.matchedLocks?.length === 1 && !data?.matchedLocks[0].remoteAccess) {
      if (data.matchedLocks[0].key) {
        eaOpen({ key: data.matchedLocks[0].key.key, lockName: data.matchedLocks[0].key.to_lock.custom_name })
      }

      const { ble } = data.matchedLocks[0]

      if (ble?.public_key) {
        open({
          lockName: data.matchedLocks[0].custom_name,
          public_key: ble.public_key,
          peripheral: ble.public_key.slice(0, 8),
          payloads: ble.command?.split('.') || ble.commands || [],
        })
      } else {
        setTimeout(() => {
          lockSelectionRef.current?.open()
        })
      }
    } else {
      setTimeout(() => {
        lockSelectionRef.current?.open()
      })
    }
  }, [loading, data, customer])

  const imgUrl = useMemo(() => {
    if (loading) return imLoading
    if (error) return imFail
    if (isOpeningOrOpened) return imOpen
    if (isDisabled) return imDisabled

    return im
  }, [loading, error, isOpeningOrOpened, customer, isDisabled])

  return (
    <IonPage id="open-lock-page">
      <DummyHeader />
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={onIonRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <IonGrid>
          {isFetchingCustomer && (
            <IonRow>
              <IonCol size="12" className="text-center flex justify-center">
                <IonSpinner />
              </IonCol>
            </IonRow>
          )}
          <IonRow>
            <IonCol size="12" className="text-center flex justify-center pt-4">
              <IonImg className="opacity-50 w-4/6" src={theme === 'dark' ? qlocxw : qlocx} />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12" className="text-center flex flex-col items-center pt-4" onClick={() => !loading && !isDisabled && !isFetchingCustomer && scan()}>
              <IonText className={`text-2xl ${loading || isOpeningOrOpened || isFetchingCustomer ? 'text-transparent' : 'text-white'} open-text`}>{isDisabled ? t('No accesses') : t('Open')}</IonText>
              <IonImg className="open-btn" src={imgUrl} />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12" className="text-center flex justify-center pt-4">
              <IonText className="text-sm" color="primary">
                {isOpeningOrOpened ? t('Opening {lockName}', { lockName: data?.activeLockName }) : t('Press open when you are within 10 meters of the door')}
              </IonText>
            </IonCol>
          </IonRow>
        </IonGrid>
        {(data?.matchedTestPeripherals?.length || 0) > 0 && (
          <IonSelect
            className="absolute bottom-0 w-full"
            ref={(ref: any) => {
              lockSelectionRef.current = ref
            }}
            cancelText={t('Cancel')}
            onIonChange={(e) => {
              const foundPeripheral = data?.matchedTestPeripherals?.find((lock) => lock === e.target.value)

              if (foundPeripheral) {
                testOpen(foundPeripheral)
              }
            }}
            onIonCancel={() => resetDevices(false)}
            interface="action-sheet"
          >
            {data?.matchedTestPeripherals?.map((p) => (
              <IonSelectOption key={p} value={p}>
                {p}
              </IonSelectOption>
            ))}
          </IonSelect>
        )}

        {(data?.matchedLocks?.length || 0) > 0 && (
          <IonSelect
            className="absolute bottom-0 w-full"
            ref={(ref: any) => {
              lockSelectionRef.current = ref
            }}
            cancelText={t('Cancel')}
            onIonChange={(e) => {
              const foundLock = data?.matchedLocks?.find((lock) => {
                const key = `${lock._id}${lock.remoteAccess ? '-remote' : ''}`

                return key === e.target.value || lock.key?.key === e.target.value
              })

              if ((foundLock?.storage && foundLock?.doors?.[0]) || (foundLock?.type === 'cluster' && foundLock?.ble?.public_key)) {
                const originalLockId = customer.allAccesses.find((a) => a.doors?.find((d) => d._id === foundLock._id))?._id
                if (originalLockId) {
                  clusterOpen(originalLockId, foundLock.custom_name)
                }
              } else if (foundLock?.ble?.public_key && !e.target.value.includes('-remote')) {
                open({
                  lockName: foundLock.custom_name,
                  public_key: foundLock.ble.public_key,
                  peripheral: foundLock.ble.public_key.slice(0, 8),
                  payloads: foundLock.ble.command?.split('.') || foundLock.ble?.commands || [],
                })
              } else if (foundLock?.remoteAccess) {
                remoteOpen({ lockId: foundLock._id, lockName: foundLock.custom_name }).then(({ successful }) => {
                  if (successful) {
                    presentAlert({
                      header: t('Remote access'),
                      message: t('Open signal sent, it usually takes 5 seconds before the door is unlocked.'),
                      buttons: [t('OK')],
                      onWillDismiss: () => resetDevices(),
                    })
                  }
                })
              } else if (foundLock?.key) {
                eaOpen({ key: foundLock.key.key, lockName: foundLock.key.to_lock.custom_name })
              } else {
                console.log('error')
              }
            }}
            onIonCancel={() => resetDevices(false)}
            interface="action-sheet"
          >
            {data?.matchedLocks?.map((lock) => {
              const ea = lock.key

              const key = ea?.key || `${lock._id}${lock.remoteAccess ? '-remote' : ''}`

              return (
                <IonSelectOption key={key} value={key}>
                  {ea?.to_lock?.custom_name || `${lock.custom_name} ${lock.remoteAccess ? t('(Remote)') : ''}`}
                </IonSelectOption>
              )
            })}
          </IonSelect>
        )}
      </IonContent>
    </IonPage>
  )
}
